import { FC, useEffect, useMemo, useState } from 'react';

import { GiftChoiceUnitData } from '@shared/api/giftChoiceUnit/giftChoiceUnit.interface';
import { getChannelBalances } from '@shared/api/giftChannelLedger/giftChannelLedger.controller';
import { ChannelBalanceSummary } from '@shared/api/giftChannelLedger/giftChannelLedger.interface';
import {
  ProxyDeliverResult,
  proxyDeliverCoupons,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  DELIVERY_CHANNEL_CONFIG,
  DELIVERY_CHANNEL_LIST,
  DeliveryChannel,
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
  GiftCouponSummary,
  formatDeliveryChannelSelectLabel,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { GiftOrderForAdmin } from '@shared/api/giftOrder/giftOrder.interface';

import { fmtPhone, redeemStatusColor, redeemStatusLabel } from './constants';
import { GiftItemEmbed } from './types';

interface ProxyDeliverModalProps {
  /** 대리발송 가능 쿠폰 (부모에서 isProxyDeliverEligible로 필터) */
  targets: GiftCouponSummary[];
  /** 주문 내 전체 쿠폰 — 기존 수신번호와의 중복 경고용 */
  orderCoupons: GiftCouponSummary[];
  giftItemEmbedMap: Map<string, GiftItemEmbed>;
  /** 선택형 상품의 choiceUnits 선택지 소스 (orderDetails.giftItem.giftItemInfo.choiceUnits populate) */
  order?: GiftOrderForAdmin;
  onClose: () => void;
}

const PHONE_REGEX = /^01[016789]\d{8}$/;
const onlyDigits = (raw: string): string => raw.replace(/\D/g, '');

const is404 = (e: unknown): boolean =>
  (e as { response?: { status?: number } })?.response?.status === 404;

interface RowState {
  included: boolean;
  phone: string;
}

/**
 * 대리발송(신청 생략 즉시발송) 모달 — 관리자가 연락처 목록을 받아 수령자 신청
 * (번호인증·서명) 없이 주문의 쿠폰들을 한 번에 발송완료 처리한다.
 * 가격·매체가 배치 공통이라 상품(giftItem) 단위로 나눠 호출한다.
 * (스펙: docs/gift-coupon-proxy-deliver-spec.md · 404 안내는 구서버 안전장치)
 */
export const ProxyDeliverModal: FC<ProxyDeliverModalProps> = ({
  targets,
  orderCoupons,
  giftItemEmbedMap,
  order,
  onClose,
}) => {
  // 상품별 그룹 — 가격이 배치 공통이므로 한 번에 한 상품씩 처리
  const itemGroups = useMemo(() => {
    const map = new Map<string, GiftCouponSummary[]>();
    for (const c of targets) {
      const list = map.get(c.giftItem);
      if (list) list.push(c);
      else map.set(c.giftItem, [c]);
    }
    return Array.from(map.entries()).map(([itemId, coupons]) => ({
      itemId,
      coupons,
      name: giftItemEmbedMap.get(itemId)?.name ?? '상품',
    }));
  }, [targets, giftItemEmbedMap]);

  const [activeItemId, setActiveItemId] = useState<string>(
    itemGroups[0]?.itemId ?? '',
  );
  const activeGroup = itemGroups.find((g) => g.itemId === activeItemId);
  const activeCoupons = useMemo(
    () => activeGroup?.coupons ?? [],
    [activeGroup],
  );

  // 선택형 상품의 choiceUnits — 실제 어떤 상품을 발송했는지 기록하기 위한 선택지.
  // populate 안 된 컨텍스트(검색 뷰 등)에선 빈 배열 → 선택 UI 미노출(기록 생략).
  const choiceUnitsByItem = useMemo(() => {
    const map = new Map<string, GiftChoiceUnitData[]>();
    for (const od of order?.orderDetails ?? []) {
      const gi = od.giftItem;
      if (!gi || typeof gi !== 'object') continue;
      const info = gi.giftItemInfo;
      const units = info && typeof info === 'object' ? info.choiceUnits : undefined;
      const populated = Array.isArray(units)
        ? units.filter(
            (u): u is GiftChoiceUnitData =>
              !!u && typeof u === 'object' && '_id' in u,
          )
        : [];
      if (populated.length > 0) map.set(gi._id, populated);
    }
    return map;
  }, [order]);
  // 상품(giftItem)별로 "실제 발송한 상품" 선택값 유지
  const [choiceUnitByItem, setChoiceUnitByItem] = useState<Record<string, string>>({});
  const activeChoiceUnits = choiceUnitsByItem.get(activeItemId) ?? [];
  const selectedChoiceUnitId = choiceUnitByItem[activeItemId] ?? '';
  const selectedChoiceUnit = activeChoiceUnits.find(
    (u) => u._id === selectedChoiceUnitId,
  );

  // 쿠폰별 행 상태 (포함 여부 + 수신번호). Requested 쿠폰은 기존 번호 고정.
  const [rows, setRows] = useState<Record<string, RowState>>(() => {
    const init: Record<string, RowState> = {};
    for (const c of targets) {
      // 답례인사 발송과 같은 번호 리스트(신청자 확인 번호)가 기본값
      init[c._id] = {
        included: true,
        phone: onlyDigits(
          c.additionalRecipientPhone ?? c.recipient?.phoneNumber ?? '',
        ),
      };
    }
    return init;
  });

  const setRow = (id: string, patch: Partial<RowState>) =>
    setRows((prev) => {
      const cur = prev[id] ?? { included: true, phone: '' };
      return { ...prev, [id]: { ...cur, ...patch } };
    });

  // 번호 목록 붙여넣기 — 줄 단위로 편집 가능한(Available) 포함 행에 순서대로 매핑
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const applyPaste = () => {
    const numbers = pasteText
      .split(/\r?\n/)
      .map(onlyDigits)
      .filter((d) => d.length >= 10);
    const editableRows = activeCoupons.filter(
      (c) =>
        c.redeemStatus === GiftCouponRedeemStatus.Available &&
        rows[c._id]?.included !== false,
    );
    if (numbers.length === 0) {
      alert('붙여넣은 내용에서 휴대폰 번호를 찾지 못했습니다.\n한 줄에 번호 하나씩 입력해주세요.');
      return;
    }
    setRows((prev) => {
      const next = { ...prev };
      editableRows.forEach((c, i) => {
        const num = numbers[i];
        if (num) {
          const cur = next[c._id];
          next[c._id] = { included: cur?.included ?? true, phone: num };
        }
      });
      return next;
    });
    if (numbers.length !== editableRows.length) {
      alert(
        `번호 ${numbers.length}개 / 입력 대상 쿠폰 ${editableRows.length}건 — 개수가 달라 앞에서부터 순서대로 채웠습니다. 매핑을 확인해주세요.`,
      );
    }
    setShowPaste(false);
  };

  // 발송 정보 (기존 DeliverModal과 동일 구성 — 배치 공통)
  const [deliveryChannel, setDeliveryChannel] = useState<DeliveryChannel | ''>('');
  const [priceWhole, setPriceWhole] = useState('');
  const [priceRebate, setPriceRebate] = useState('');
  const [amountConfirmed, setAmountConfirmed] = useState(false);

  // 채널 충전금 미러 잔액 — 발송 직전 부족 여부 대조용 (DeliverModal과 동일 표시).
  // 구서버(원장 미배포)면 404 → null 유지, 잔액 영역만 조용히 숨김
  const [channelBalances, setChannelBalances] = useState<
    ChannelBalanceSummary[] | null
  >(null);
  useEffect(() => {
    getChannelBalances()
      .then(setChannelBalances)
      .catch(() => setChannelBalances(null));
  }, []);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProxyDeliverResult | null>(null);

  const checkedCoupons = useMemo(
    () => activeCoupons.filter((c) => rows[c._id]?.included !== false),
    [activeCoupons, rows],
  );

  // 판매가: 구매 당시 동결값(salePriceRetail) 우선, 없으면 주문 스냅샷 가격
  const priceRetail =
    checkedCoupons.find((c) => c.salePriceRetail != null)?.salePriceRetail ??
    giftItemEmbedMap.get(activeItemId)?.priceRetail ??
    0;

  const wholeNum = Number(priceWhole);
  const hasValidWhole = priceWhole !== '' && !isNaN(wholeNum) && wholeNum > 0;
  const totalAmount = hasValidWhole ? wholeNum * checkedCoupons.length : 0;

  // 선택 매체의 미러 잔액 — 발송 직전 실수(도매가 오입력·충전 부족)를 잡는 핵심 대조값
  const selectedBalance =
    deliveryChannel && channelBalances
      ? channelBalances.find((b) => b.channel === deliveryChannel)
      : undefined;
  const afterBalance =
    selectedBalance && hasValidWhole ? selectedBalance.balance - totalAmount : null;

  // 중복 경고 — 배치 내 중복 + 주문 내 기존 수신번호(교환신청/발송완료)와 중복. 차단 아님.
  const duplicateWarnings = useMemo(() => {
    const warnings: string[] = [];
    const batchPhones = new Map<string, number>();
    for (const c of checkedCoupons) {
      const d = onlyDigits(rows[c._id]?.phone ?? '');
      if (d) batchPhones.set(d, (batchPhones.get(d) ?? 0) + 1);
    }
    for (const [digits, count] of Array.from(batchPhones.entries())) {
      if (count > 1)
        warnings.push(`${fmtPhone(digits)} — 이번 발송에 ${count}건 중복`);
    }
    const checkedIds = new Set(checkedCoupons.map((c) => c._id));
    const existing = new Map<string, number>();
    for (const c of orderCoupons) {
      if (checkedIds.has(c._id)) continue;
      if (
        c.redeemStatus !== GiftCouponRedeemStatus.Requested &&
        c.redeemStatus !== GiftCouponRedeemStatus.Delivered
      )
        continue;
      const d = onlyDigits(c.recipient?.phoneNumber ?? '');
      if (d) existing.set(d, (existing.get(d) ?? 0) + 1);
    }
    for (const digits of Array.from(batchPhones.keys())) {
      const count = existing.get(digits);
      if (count)
        warnings.push(
          `${fmtPhone(digits)} — 이 주문에서 이미 신청/발송된 번호 (${count}건)`,
        );
    }
    return warnings;
  }, [checkedCoupons, rows, orderCoupons]);

  const validate = (): string | null => {
    if (checkedCoupons.length === 0) return '선택된 쿠폰이 없습니다.';
    for (const c of checkedCoupons) {
      if (c.redeemStatus !== GiftCouponRedeemStatus.Available) continue;
      const digits = onlyDigits(rows[c._id]?.phone ?? '');
      if (!PHONE_REGEX.test(digits))
        return `#${c.seq} (${c.code}) 수신번호가 올바르지 않습니다. (예: 01012345678)`;
    }
    if (activeChoiceUnits.length > 0 && !selectedChoiceUnitId)
      return '선택형 상품입니다. 실제 발송한 상품을 선택해주세요.';
    if (!deliveryChannel) return '발송 매체를 선택해주세요.';
    if (!hasValidWhole) return '도매가를 입력해주세요.';
    if (priceRebate.trim() !== '' && isNaN(Number(priceRebate)))
      return '리베이트는 숫자로 입력해주세요.';
    if (!amountConfirmed)
      return `총 발주비용(${totalAmount.toLocaleString()}원)이 맞는지 확인 후 체크해주세요.`;
    return null;
  };

  const handleSubmit = async () => {
    const message = validate();
    if (message) {
      alert(message);
      return;
    }
    const productLabel = `${activeGroup?.name ?? '상품'}${
      selectedChoiceUnit ? ` — ${selectedChoiceUnit.name}` : ''
    }`;
    if (
      !window.confirm(
        `[${productLabel}] ${checkedCoupons.length}건을 수령자 신청(번호인증·서명) 없이 바로 발송완료 처리합니다.\n실제 기프티콘은 매체에서 별도 발송해야 합니다. 진행할까요?`,
      )
    )
      return;

    setSubmitting(true);
    setError(null);
    try {
      const res = await proxyDeliverCoupons({
        entries: checkedCoupons.map((c) =>
          c.redeemStatus === GiftCouponRedeemStatus.Available
            ? {
                couponId: c._id,
                phoneNumber: onlyDigits(rows[c._id]?.phone ?? ''),
                // 선택형 상품이면 실제 발송한 상품을 기록 (Requested는 수령자 선택 유지)
                selectedChoiceUnit: selectedChoiceUnitId || undefined,
              }
            : { couponId: c._id },
        ),
        priceWhole: wholeNum,
        priceRetail,
        priceRebate: priceRebate.trim() === '' ? 0 : Number(priceRebate),
        deliveryChannel: deliveryChannel as DeliveryChannel,
      });
      setResult(res);
    } catch (e: unknown) {
      if (is404(e)) {
        setError(
          '서버에 아직 반영되지 않은 기능입니다. 백엔드 배포 후 사용할 수 있어요. (스펙: docs/gift-coupon-proxy-deliver-spec.md)',
        );
      } else {
        setError(e instanceof Error ? e.message : '대리발송 처리에 실패했습니다.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative flex max-h-[88vh] w-full max-w-2xl flex-col gap-4 overflow-y-auto rounded-xl bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-base font-bold text-rose-800">
              대리 일괄발송 (신청 생략)
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              답례인사 발송과 같은 번호 목록(신청자 확인 번호 보유 쿠폰)에, 안내
              대신 신청 절차 없이 상품을 바로 발송완료 처리합니다. 번호 없는 쿠폰과
              &quot;나중에 받기&quot; 예약 건은 표시되지 않습니다. 처리 건에는
              &quot;대리발송&quot; 기록이 남습니다.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {!result ? (
          <>
            {/* 상품 선택 — 가격이 배치 공통이라 상품별로 나눠 처리 */}
            {itemGroups.length > 1 && (
              <div className="flex flex-wrap gap-1.5">
                {itemGroups.map((g) => (
                  <button
                    key={g.itemId}
                    onClick={() => {
                      setActiveItemId(g.itemId);
                      setAmountConfirmed(false);
                    }}
                    className={`rounded-full border px-3 py-1 text-xs font-medium ${
                      g.itemId === activeItemId
                        ? 'border-rose-400 bg-rose-50 text-rose-700'
                        : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {g.name} ({g.coupons.length})
                  </button>
                ))}
              </div>
            )}
            {itemGroups.length > 1 && (
              <p className="-mt-2 text-[11px] text-gray-400">
                가격·매체가 일괄 적용되므로 상품별로 나눠서 발송 처리합니다.
              </p>
            )}

            {/* 발송 상품 — 무엇을 보내는지 항상 표시 */}
            <div className="rounded-lg border border-rose-100 bg-rose-50/40 px-3 py-2">
              <p className="text-[11px] font-semibold text-rose-400">발송 상품</p>
              <p className="mt-0.5 text-sm font-semibold text-gray-800">
                {activeGroup?.name ?? '상품'}
                <span className="ml-1.5 text-xs font-normal text-gray-500">
                  × {checkedCoupons.length}건
                </span>
              </p>
              {activeChoiceUnits.length > 0 && (
                <div className="mt-2">
                  <label className="block text-xs font-medium text-gray-600">
                    실제 발송한 상품 (선택형){' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedChoiceUnitId}
                    onChange={(e) =>
                      setChoiceUnitByItem((prev) => ({
                        ...prev,
                        [activeItemId]: e.target.value,
                      }))
                    }
                    className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                  >
                    <option value="" disabled>
                      발송한 상품 선택
                    </option>
                    {activeChoiceUnits.map((u) => (
                      <option key={u._id} value={u._id}>
                        {u.name}
                        {u.priceRegular
                          ? ` (${u.priceRegular.toLocaleString()}원)`
                          : ''}
                        {u.enabled === false ? ' — 판매중지' : ''}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-[11px] text-gray-400">
                    선택형 상품은 수령자가 고르는 대신 관리자가 발송한 상품을
                    기록합니다. 쿠폰의 &quot;선택상품&quot;으로 남습니다.
                  </p>
                </div>
              )}
            </div>

            {/* 쿠폰별 수신번호 입력 */}
            <div className="rounded-lg border border-gray-200">
              <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
                <p className="text-xs font-semibold text-gray-600">
                  발송 대상 {checkedCoupons.length}건 / {activeCoupons.length}건
                </p>
                <button
                  onClick={() => setShowPaste((v) => !v)}
                  className="text-xs font-medium text-rose-600 hover:underline"
                >
                  {showPaste ? '붙여넣기 닫기' : '번호 목록 붙여넣기'}
                </button>
              </div>

              {showPaste && (
                <div className="border-b border-gray-100 bg-rose-50/40 p-3">
                  <textarea
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                    rows={4}
                    placeholder={'01012345678\n01098765432\n… (한 줄에 하나씩 — 위에서부터 순서대로 채워집니다)'}
                    className="w-full resize-none rounded-lg border px-3 py-2 text-sm focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                  />
                  <div className="mt-1.5 flex justify-end">
                    <button
                      onClick={applyPaste}
                      className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700"
                    >
                      순서대로 채우기
                    </button>
                  </div>
                </div>
              )}

              <ul className="max-h-56 divide-y divide-gray-50 overflow-y-auto">
                {activeCoupons.map((c) => {
                  const row = rows[c._id] ?? { included: true, phone: '' };
                  const isAvailable =
                    c.redeemStatus === GiftCouponRedeemStatus.Available;
                  const digits = onlyDigits(row.phone);
                  const phoneInvalid =
                    row.included && isAvailable && digits !== '' && !PHONE_REGEX.test(digits);
                  return (
                    <li
                      key={c._id}
                      className={`flex items-center gap-2 px-3 py-1.5 ${
                        row.included ? '' : 'opacity-40'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={row.included}
                        onChange={(e) => setRow(c._id, { included: e.target.checked })}
                        className="h-4 w-4 shrink-0 rounded border-gray-300 text-rose-600 focus:ring-rose-500"
                      />
                      <span className="w-20 shrink-0 text-xs tabular-nums text-gray-500">
                        #{c.seq} · {c.code}
                      </span>
                      <span
                        className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${redeemStatusColor[c.redeemStatus]}`}
                      >
                        {redeemStatusLabel[c.redeemStatus]}
                      </span>
                      {c.shareStatus !== GiftCouponShareStatus.NotShared && (
                        <span
                          className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
                          title="이미 공유된 쿠폰입니다. 수령자가 직접 신청할 수 있으니 중복 발송에 주의하세요."
                        >
                          공유됨
                        </span>
                      )}
                      {isAvailable ? (
                        <input
                          type="tel"
                          value={row.phone}
                          onChange={(e) => setRow(c._id, { phone: e.target.value })}
                          placeholder="수신번호 (01012345678)"
                          disabled={!row.included}
                          className={`ml-auto w-44 rounded-md border px-2 py-1 text-sm tabular-nums focus:outline-none focus:ring-1 ${
                            phoneInvalid
                              ? 'border-red-400 focus:border-red-500 focus:ring-red-500'
                              : 'border-gray-300 focus:border-rose-500 focus:ring-rose-500'
                          }`}
                        />
                      ) : (
                        <span className="ml-auto flex min-w-0 items-center gap-1.5">
                          {c.selectedChoiceUnit &&
                            typeof c.selectedChoiceUnit === 'object' && (
                              <span
                                className="max-w-[120px] truncate text-[10px] text-gray-400"
                                title={`수령자가 선택한 상품: ${c.selectedChoiceUnit.name}`}
                              >
                                {c.selectedChoiceUnit.name}
                              </span>
                            )}
                          <span
                            className="shrink-0 text-sm tabular-nums text-gray-600"
                            title="이미 교환신청된 쿠폰 — 기존 신청 번호로 발송됩니다."
                          >
                            {fmtPhone(c.recipient?.phoneNumber)}
                          </span>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            {duplicateWarnings.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                <p className="text-xs font-semibold text-amber-700">
                  중복 번호 확인 (차단은 아님 — 한 사람이 여러 장 수령이면 정상)
                </p>
                <ul className="mt-1 list-inside list-disc text-xs text-amber-700">
                  {duplicateWarnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* 발송 정보 — 기존 발송 처리와 동일 구성 */}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  발송 매체 (deliveryChannel) <span className="text-red-500">*</span>
                </label>
                <select
                  value={deliveryChannel}
                  onChange={(e) =>
                    setDeliveryChannel(e.target.value as DeliveryChannel | '')
                  }
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                >
                  <option value="" disabled>
                    발송 매체 선택
                  </option>
                  {DELIVERY_CHANNEL_LIST.map((ch) => (
                    <option key={ch} value={ch}>
                      {formatDeliveryChannelSelectLabel(ch)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  판매가 (priceRetail)
                </label>
                <div className="mt-1 flex items-center rounded-md border bg-gray-50 px-3 py-2 text-sm text-gray-500">
                  {priceRetail.toLocaleString()}원
                  <span className="ml-2 text-xs text-gray-400">(자동)</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  도매가 (priceWhole) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={priceWhole}
                  onChange={(e) => {
                    setPriceWhole(e.target.value);
                    setAmountConfirmed(false);
                  }}
                  placeholder="도매가 입력"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  리베이트 (priceRebate){' '}
                  <span className="text-xs font-normal text-gray-400">
                    (선택 · 미입력 시 0)
                  </span>
                </label>
                <input
                  type="number"
                  value={priceRebate}
                  onChange={(e) => setPriceRebate(e.target.value)}
                  placeholder="0"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                />
              </div>

              <div className="col-span-2 rounded-md border border-amber-300 bg-amber-50 p-3">
                {hasValidWhole && checkedCoupons.length > 0 ? (
                  <>
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        총 발주비용 ({checkedCoupons.length}건)
                      </span>
                      <span className="text-lg font-bold text-amber-700">
                        {totalAmount.toLocaleString()}원
                      </span>
                    </div>
                    <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-800">
                      <input
                        type="checkbox"
                        checked={amountConfirmed}
                        onChange={(e) => setAmountConfirmed(e.target.checked)}
                        className="h-4 w-4 shrink-0 rounded border-gray-300 text-rose-600 focus:ring-rose-500"
                      />
                      <span>이 금액이 맞습니다.</span>
                    </label>
                  </>
                ) : (
                  <p className="text-sm text-gray-500">
                    {checkedCoupons.length === 0
                      ? '발송할 쿠폰을 선택해주세요'
                      : '도매가 입력 필요'}
                  </p>
                )}
              </div>

              {selectedBalance && (
                <div
                  className={`col-span-2 rounded-md border p-3 text-sm ${
                    afterBalance !== null && afterBalance < 0
                      ? 'border-red-300 bg-red-50'
                      : 'border-indigo-200 bg-indigo-50/60'
                  }`}
                >
                  <div className="flex items-baseline justify-between">
                    <span className="text-gray-600">
                      현재 {DELIVERY_CHANNEL_CONFIG[selectedBalance.channel].label}{' '}
                      잔액
                    </span>
                    <span
                      className={`font-semibold ${
                        selectedBalance.balance < 0 ? 'text-red-600' : 'text-gray-900'
                      }`}
                    >
                      {selectedBalance.balance.toLocaleString()}원
                    </span>
                  </div>
                  {afterBalance !== null ? (
                    <div className="mt-1 flex items-baseline justify-between">
                      <span className="text-gray-600">발송 후 잔액</span>
                      <span
                        className={`font-bold ${
                          afterBalance < 0 ? 'text-red-600' : 'text-indigo-700'
                        }`}
                      >
                        {afterBalance.toLocaleString()}원
                      </span>
                    </div>
                  ) : (
                    <p className="mt-1 text-xs text-gray-400">
                      도매가를 입력하면 발송 후 잔액이 표시됩니다.
                    </p>
                  )}
                  {afterBalance !== null && afterBalance < 0 && (
                    <p className="mt-1 text-xs font-medium text-red-600">
                      잔액 부족 — 도매가 오입력이 아닌지, 충전·입금 기록이 빠지지
                      않았는지 확인하세요. (발송이 차단되지는 않습니다)
                    </p>
                  )}
                </div>
              )}
            </div>

            {error && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                disabled={submitting}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              >
                닫기
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || checkedCoupons.length === 0}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-bold text-white hover:bg-rose-700 disabled:opacity-50"
              >
                {submitting
                  ? '처리중…'
                  : `${checkedCoupons.length}건 대리발송`}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="rounded-lg bg-emerald-50 px-4 py-5 text-center">
              <p className="text-sm text-emerald-600">대리발송 완료</p>
              <p className="mt-1 text-2xl font-bold text-emerald-700">
                {result.deliveredCount}건
              </p>
              <p className="mt-1 text-sm font-medium text-gray-700">
                {activeGroup?.name ?? '상품'}
                {selectedChoiceUnit ? ` — ${selectedChoiceUnit.name}` : ''}
              </p>
              <p className="mt-2 text-xs text-gray-500">
                실제 기프티콘은 선택한 매체에서 입력한 수신번호로 발송해주세요.
              </p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => window.location.reload()}
                className="rounded-lg bg-rose-600 px-5 py-2 text-sm font-bold text-white hover:bg-rose-700"
              >
                확인
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
